/* Dimension callouts: the drafting figure the core lays out for every dimensioned constraint.
 *
 * Extension lines, a dimension line between two arrowheads, a radial leader, an angular arc and
 * the number beside it are all geometry, so all of it comes from `gcs-core/src/callout.rs` in
 * world coordinates.  This module is a proxy over that one call; the view maps the points to the
 * screen with the same transform it draws the sketch through. */
import type { Constraint } from './constraints.js';
import { Sketch } from './model.js';
import { core, takeJson, withBuf } from './wasm.js';

export type Pt = [number, number];
export type Seg = [Pt, Pt];

export interface CalloutArc {
  c: Pt;
  r: number;
  /** The sweep runs counterclockwise when `a1 > a0` — the sign of the angle constrained. */
  a0: number;
  a1: number;
}

/** `at` is the tip and `dir` the direction it points, so the head fills the arrow length back
 *  along `-dir`. */
export interface Arrow {
  at: Pt;
  dir: Pt;
}

export interface Callout {
  /** The constraint this dimension states — what clicking it selects. */
  id: number;
  /** The view its figure is laid out in: -1 the page, a plane's index, -2 none — its points
   *  stand in views that are not one place in space, so it has nowhere to be drawn. */
  view: number;
  text: string;
  /** The centre of the label's box, the direction it reads in, and the box's four corners. */
  anchor: Pt;
  angle: number;
  label: Pt[];
  /** The dimension line itself, and the extension/witness lines a drawing puts in dashes. */
  solid: Seg[];
  thin: Seg[];
  arcs: CalloutArc[];
  arrows: Arrow[];
}

export interface Callouts {
  /** The size the layout reserved for the text, in screen pixels, and the length and barb
   *  half-width (as a fraction of that length) of an arrowhead.  Drawing the heads at any other
   *  shape would make a second front end invent its own drafting style. */
  font: number;
  arrow: number;
  barb: number;
  items: Callout[];
}

/** Every dimension in the sketch, drawn.  `unit` is the world length of one screen pixel, which
 *  is what makes the stand-offs, arrowheads and characters come out the same size at any zoom.
 *  `ids` requests only those constraint annotations, with no lanes reserved by other values.
 *  Omit it only for the explicit all-constraints inspection overlay. */
export function callouts(sk: Sketch, unit: number, ids?: readonly number[]): Callouts {
  if (ids) return withBuf(ids.length, 4, (b) => {
    b.set(ids);
    return takeJson<Callouts>(core().gcs_selected_callouts_json(sk.handle, unit, b.ptr, ids.length));
  });
  return takeJson<Callouts>(core().gcs_callouts_json(sk.handle, unit));
}

/** Take hold of a callout at a world point: the two numbers to hand back to `drag` for the rest
 *  of the gesture, so the callout moves with the pointer instead of jumping to it. */
export function grab(sk: Sketch, unit: number, id: number, x: number, y: number)
  : [number, number] | null {
  return withBuf(2, 8, (b) => (core().gcs_callout_grab(sk.handle, id, unit, x, y, b.ptr)
    ? [b.f64[0], b.f64[1]] as [number, number]
    : null));
}

/** Move a callout so the point it was grabbed at follows the pointer to (x, y). */
export function drag(sk: Sketch, id: number, x: number, y: number,
                     grip: readonly [number, number]): void {
  core().gcs_callout_drag(sk.handle, id, x, y, grip[0], grip[1]);
}

/** Put a callout back wherever the layout would have put it; true if it had been moved at all,
 *  so a caller can tell an edit from a no-op. */
export function reset(sk: Sketch, id: number): boolean {
  return core().gcs_callout_reset(sk.handle, id) !== 0;
}

/** The three dimensions between two points: the length (`Distance`), and the run and the rise —
 *  an `Ordinate` along the view's `u` and `v`. */
export type PairDimension = 'length' | 'run' | 'rise';

/** Which of the three dimensions between two points a callout dropped at `at` states: the
 *  length between them, the run, or the rise.  Where the number is put is what says which
 *  measurement was meant — see `callout::pair_dimension`, which is also what draws the figure
 *  that results, so the two cannot disagree. */
export function pairDimension(a: readonly [number, number], b: readonly [number, number],
                              at: readonly [number, number]): PairDimension {
  const i = core().gcs_dimension_pair_kind(a[0], a[1], b[0], b[1], at[0], at[1]);
  return i === 1 ? 'run' : i === 2 ? 'rise' : 'length';
}

/** Which of the three a constraint between two points is, the way `pairDimension` names it. */
export function pairOf(c: Constraint): PairDimension | null {
  if (c.typeName === 'Distance') return 'length';
  if (c.typeName !== 'Ordinate') return null;
  return c.along === 'u' ? 'run' : c.along === 'v' ? 'rise' : null;
}
